import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTradeSizeDistribution } from '../../hooks/useTradeSizeDistribution';
import './BigVsSmall.css';
import '../SkeletonTradeRow/SkeletonTradeRow.css';

const FORMAT_RANGE_LABELS = {
  '10K-100K': '$10K - $100K',
  '100K-500K': '$100K - $500K',
  '500K-1M': '$500K - $1M',
  '1M-10M': '$1M - $10M',
  '10M+': '$10M+',
};
const defaultFormatRange = (range) => FORMAT_RANGE_LABELS[range] || range;

/**
 * BigVsSmall component
 * @param {string} variant - 'default' for Bubbles page, 'compact' for Trades page
 * @param {object} distributionData - pre-fetched data from useDashboardStats (optional)
 * @param {number} distributionTotalTrades - pre-fetched total (optional)
 * @param {boolean} distributionLoading - loading state from parent (optional)
 */
function BigVsSmall({ variant = 'default', distributionData, distributionTotalTrades, distributionLoading }) {
  const { t } = useTranslation();
  const hasExternalData = distributionData !== undefined;
  const ownHook = useTradeSizeDistribution(24, 10000, { enabled: !hasExternalData });
  const data = hasExternalData ? distributionData : ownHook.data;
  const loading = hasExternalData ? (distributionLoading ?? false) : ownHook.loading;
  const error = hasExternalData ? null : ownHook.error;
  const totalTrades = hasExternalData ? (distributionTotalTrades ?? 0) : ownHook.totalTrades;
  const formatRange = defaultFormatRange;

  const isCompact = variant === 'compact';

  // Define ranges in order
  const ranges = ['10K-100K', '100K-500K', '500K-1M', '1M-10M', '10M+'];

  // Calculate total percentage across all ranges
  const totalPercentage = useMemo(() => {
    if (!data) return { long: 50, short: 50, totalLong: 0, totalShort: 0 };

    let totalLong = 0;
    let totalShort = 0;

    ranges.forEach((range) => {
      const rangeData = data[range];
      if (rangeData) {
        // Use count for more accurate calculation
        const total = rangeData.count || 0;
        const longPercent = rangeData.long || 50;
        const shortPercent = rangeData.short || 50;

        // Estimate actual long/short counts based on percentages
        totalLong += Math.round((longPercent / 100) * total);
        totalShort += Math.round((shortPercent / 100) * total);
      }
    });

    const grandTotal = totalLong + totalShort;
    return {
      long: grandTotal > 0 ? Math.round((totalLong / grandTotal) * 100) : 50,
      short: grandTotal > 0 ? Math.round((totalShort / grandTotal) * 100) : 50,
      totalLong,
      totalShort,
    };
  }, [data, ranges]);

  const containerClass = isCompact ? 'bvs-card' : 'big-vs-small-container';

  // Non-compact: keep original loading/error states
  if (!isCompact && loading) {
    return (
      <div className={containerClass}>
        <h3 className="big-vs-small-title">{t('bubbles.bigVsSmall', 'Big vs Small')}</h3>
        <div className="big-vs-small-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <h3 className={isCompact ? 'bvs-title' : 'big-vs-small-title'}>{t('bubbles.bigVsSmall', 'Big vs Small')}</h3>
        <div className={isCompact ? 'bvs-error' : 'big-vs-small-error'}>{error}</div>
      </div>
    );
  }

  if (!isCompact && !data) {
    return null;
  }

  // Compact variant (for Trades page) — renders layout immediately, shimmer bars while loading
  if (isCompact) {
    const isLoading = loading || !data;

    return (
      <div className="bvs-card">
        <div className="bvs-header">
          <div className="bvs-title">{t('bubbles.bigVsSmall', 'Big vs Small')}</div>
          <div className="bvs-subtitle">{t('bubbles.tradeSizeDistribution', 'Trade Size Distribution (24h)')}</div>
        </div>

        <div className="bvs-ranges">
          {ranges.map((range) => {
            const rangeData = !isLoading && data?.[range];
            const long = rangeData ? rangeData.long : 0;
            const short = rangeData ? rangeData.short : 0;
            const count = rangeData ? rangeData.count : 0;

            return (
              <div key={range} className="bvs-range-item">
                <div className="bvs-range-label">{formatRange(range)}</div>
                <div className="bvs-bar-container">
                  {isLoading ? (
                    <div className="bvs-bar-skeleton" />
                  ) : (
                    <>
                      <div className="bvs-bar-long" style={{ width: `${long}%` }}>
                        {long > 15 && <span className="bvs-bar-percentage">{long}%</span>}
                      </div>
                      <div className="bvs-bar-short" style={{ width: `${short}%` }}>
                        {short > 15 && <span className="bvs-bar-percentage">{short}%</span>}
                      </div>
                    </>
                  )}
                </div>
                <div className="bvs-trades-count">
                  {isLoading ? <span className="skeleton-bone-inline" /> : count.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bvs-divider"></div>

        <div className="bvs-total-section">
          <div className="bvs-total-label">{t('bubbles.totalPercentage', 'Total')}</div>
          <div className="bvs-bar-container">
            {isLoading ? (
              <div className="bvs-bar-skeleton" />
            ) : (
              <>
                <div className="bvs-bar-long" style={{ width: `${totalPercentage.long}%` }}>
                  <span className="bvs-bar-percentage">{totalPercentage.long}%</span>
                </div>
                <div className="bvs-bar-short" style={{ width: `${totalPercentage.short}%` }}>
                  <span className="bvs-bar-percentage">{totalPercentage.short}%</span>
                </div>
              </>
            )}
          </div>
          <div className="bvs-trades-count"></div>
        </div>

        <div className="bvs-legend">
          <div className="bvs-legend-item">
            <div className="bvs-legend-dot long"></div>
            <span>{t('bubbles.long', 'Long')}</span>
          </div>
          <div className="bvs-legend-item">
            <div className="bvs-legend-dot short"></div>
            <span>{t('bubbles.short', 'Short')}</span>
          </div>
        </div>

        <div className="bvs-total-trades">
          {isLoading ? <span className="skeleton-bone-inline" /> : <>{t('bubbles.totalTrades', 'Total')}: {totalTrades.toLocaleString()} trades</>}
        </div>
      </div>
    );
  }

  // Default variant (for Bubbles page)
  return (
    <div className="big-vs-small-container">
      <h3 className="big-vs-small-title">{t('bubbles.bigVsSmall', 'Big vs Small')}</h3>
      <p className="big-vs-small-subtitle">
        {t('bubbles.tradeSizeDistribution', 'Trade Size Distribution (24h)')}
      </p>

      <div className="big-vs-small-ranges">
        {ranges.map((range) => {
          const rangeData = data[range];
          if (!rangeData) return null;

          const { long, short, count } = rangeData;

          return (
            <div key={range} className="range-row">
              <div className="range-label">
                <span className="range-name">{formatRange(range)}</span>
                <span className="range-count">{count} trades</span>
              </div>
              <div className="range-bar-container">
                <div
                  className="range-bar long"
                  style={{ width: `${long}%` }}
                >
                  {long > 10 && <span className="bar-label">{long}%</span>}
                </div>
                <div
                  className="range-bar short"
                  style={{ width: `${short}%` }}
                >
                  {short > 10 && <span className="bar-label">{short}%</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Percentage */}
      <div className="big-vs-small-total">
        <div className="total-label">
          <span className="total-title">{t('bubbles.totalPercentage', 'TOTAL')}</span>
        </div>
        <div className="range-bar-container total-bar-container">
          <div
            className="range-bar long"
            style={{ width: `${totalPercentage.long}%` }}
          >
            <span className="bar-label">{totalPercentage.long}%</span>
          </div>
          <div
            className="range-bar short"
            style={{ width: `${totalPercentage.short}%` }}
          >
            <span className="bar-label">{totalPercentage.short}%</span>
          </div>
        </div>
      </div>

      <div className="big-vs-small-legend">
        <div className="legend-item">
          <span className="legend-color long"></span>
          <span>{t('bubbles.long', 'Long')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-color short"></span>
          <span>{t('bubbles.short', 'Short')}</span>
        </div>
      </div>

      <div className="big-vs-small-footer">
        <span>{t('bubbles.totalTrades', 'Total')}: {totalTrades.toLocaleString()} trades</span>
      </div>
    </div>
  );
}

export default BigVsSmall;
